import { el } from "date-fns/locale";
import Blue from "~/core";

new Blue({
  name: 'Subscription Center Tabs',
  selector: ".sub-center__tabs",
  setup(tabsWrapper) {
    const tabs = tabsWrapper.querySelectorAll(".sub-center__tabs-tab");
    const panels = tabsWrapper.parentElement.querySelectorAll(".sub-center__tabs-panel");

    const keyActions = {
      ArrowRight(idx, length, activateTab) {
        activateTab((idx + 1) % length, true);
      },
      ArrowLeft(idx, length, activateTab) {
        activateTab((idx - 1 + length) % length, true);
      },
      Home(idx_, length, activateTab) {
        activateTab(0, true);
      },
      End(idx, length, activateTab) {
        activateTab(length - 1, true);
      }
    };

    function activateTab(index, focus = true) {
      tabs.forEach((tab, i) => toggleTab(tab, i === index));
      panels.forEach((panel, i) => togglePanel(panel, i === index));
      if (focus) tabs[index].focus();
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => activateTab(index, true));
    });

    tabsWrapper.addEventListener("keydown", e => {
      const length = tabs.length;
      let idx = getActiveTabIndex(tabs);

      if (!document.activeElement.closest(".sub-center__tabs-tab")) return;

      const action = keyActions[e.key];

      if (action) {
        e.preventDefault();
        action(idx, length, activateTab);
      }
    });
  }
});

new Blue({
  name: 'Subscription Center Sign In',
  selector: ".sub-center__sign-in",
  setup(form) {
    const id = form.id;
    const button = form.querySelector("button");
    const input = form.querySelector("input");
    const confirmation = form.closest('.sub-center__content').querySelector(`.sub-center__email-response[data-for=${id}]`);
    const error = form.parentElement.querySelector(".sub-center__form-error");

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      toggleSignIn(button, input, false);
      error.setAttribute("hidden", "");

      const formData = new FormData(form);
      const response = await fetch(form.action, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        confirmation.removeAttribute("hidden");
      } else {
        toggleSignIn(button, input, true);
        const body = await response.json().catch(() => null);
        error.textContent = body?.error ?? "An error occurred. Please try again.";
        error.removeAttribute("hidden");
      }
    });
  }
});

new Blue({
  name: 'Subscription Center Options',
  selector: ".sub-center__option-list",
  setup(form) {
    const options = form.querySelectorAll(".sub-center__option");
    const checkboxes = form.querySelectorAll(".sub-center__option-checkbox input");
    const saveChangesButton = document.querySelector(".sub-center__save-changes");
    const selectAll = form.parentElement.querySelector("#sub-select-all");
    const deselectAll = form.parentElement.querySelector("#sub-deselect-all");
    const flyout = document.querySelector(".sub-center__flyout");
    const flyoutCloseButton = flyout.querySelector(".sub-center__flyout-close");
    const toggles = form.querySelectorAll(".sub-center__options-toggle");

    let flyoutQueue = [];
    let flyoutTimer = null;

    if (flyout.dataset.showOnload) {
      triggerFlyout(flyout.dataset.showOnload);
    }

    createEventListeners();
    initToggles();

    options.forEach(option => {
      handleDialog(option, saveChangesButton);
    });


    function initToggles() {
      toggles.forEach(toggle => {
        const container = toggle.nextElementSibling;
        let open = toggle.getAttribute('aria-expanded') === "true";

        toggle.addEventListener('click', e => {
          open = !open;

          const height = open ? container.scrollHeight : 0;
          container.style.height = height + "px";
          toggle.setAttribute('aria-expanded', String(open));
          toggle.classList.toggle("active", open);

          container.addEventListener('transitionend', () => {
            if (e.propertyName === 'height')
              panel.style.height = open ? 'auto' : undefined;
          }, {once: true});
        })
      })
    }

    function createEventListeners() {
      selectAll.addEventListener("click", () => {
        checkboxes.forEach(checkbox => {
          checkbox.checked = true;
        });
        handleStateChange();
      });

      deselectAll.addEventListener("click", () => {
        checkboxes.forEach(checkbox => {
          checkbox.checked = false;
        });
        handleStateChange();
      });

      checkboxes.forEach(checkbox => {
        checkbox.addEventListener("change", () => {
          handleStateChange();
        });
      });

      saveChangesButton.addEventListener("click", async () => {
        saveChangesButton.setAttribute("disabled", "");
        saveChangesButton.dataset.loading = "true";

        const formData = new FormData(form);
        const response = await fetch(form.action, {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          triggerFlyout('updated');
        } else {
          console.error("Failed to save changes.");
        }

        saveChangesButton.dataset.loading = "false";
      });

      flyoutCloseButton.addEventListener("click", () => {
        if (flyoutTimer) {
          clearTimeout(flyoutTimer);
          flyoutTimer = null;
        }
        closeFlyout();
      });
    }

    function handleStateChange() {
      if (saveChangesButton.dataset.loading !== "true")
        saveChangesButton.removeAttribute("disabled");

      countSelected()
    }

    function countSelected() {
      toggles.forEach(toggle => {
        const optionsList = toggle.nextElementSibling;
        const checkboxes = optionsList.querySelectorAll('input[type="checkbox"]');
        const checkedCount = Array.from(checkboxes).filter(cb => cb.checked).length;
        const totalCount = checkboxes.length;
        const span = toggle.querySelector('span');
        if (span) {
          span.textContent = `(${checkedCount}/${totalCount})`;
        }
      });
    }

    function triggerFlyout(id) {
      if (flyout.isActive) {
        flyoutQueue.push(id);
        return;
      }

      flyout.isActive = true;

      flyout.querySelectorAll(`.sub-center__flyout-content`).forEach(content => {
        content.setAttribute("hidden", "");
      });

      const flyoutContent = flyout.querySelector(`.sub-center__flyout-content[data-id="${id}"`);
      if (!flyoutContent)  {
        flyout.isActive = false;
        return;
      }

      flyoutContent.removeAttribute("hidden");
      flyout.classList.add("showing");

      flyoutTimer = setTimeout(closeFlyout, 10000);
    }

    function closeFlyout() {
      flyout.classList.remove("showing");

      if (flyoutQueue.length > 0) {
        const nextId = flyoutQueue.shift();
        setTimeout(() => {
          flyout.isActive = false;
          triggerFlyout(nextId);
        }, 1000);
      }
      else {
        flyout.isActive = false;
      }
    }
  }
});

function handleDialog(option, saveChangesButton) {
  const dialog = option.querySelector('.sub-center__option-dialog');
  if (!dialog) return;

  const openButton = option.querySelector('.sub-center__option-dialog-open');
  const closeButton = dialog.querySelectorAll('.sub-center__option-dialog-close');
  const toggleButton = option.querySelector('.sub-center__option-dialog-toggle');
  const checkbox = option.querySelector('.sub-center__option-checkbox input');

  let checkboxState = null;

  openButton.addEventListener('click', () => {
    checkboxState = checkbox.checked;
    updateDialogContent(dialog, checkbox.checked);
    dialog.showModal();
  });

  closeButton.forEach(button => {
    button.addEventListener('click', () => {
      if (button.classList.contains("cancel"))
        checkbox.checked = checkboxState;

      checkboxState = null;
      dialog.close();
    });
  });

  toggleButton.addEventListener('click', () => {
    checkbox.checked = !checkbox.checked;
    updateDialogContent(dialog, checkbox.checked);
    saveChangesButton.removeAttribute("disabled");
  });
}

function updateDialogContent(dialog, isChecked) {
  dialog.classList.toggle("sub-center__option-dialog--checked", isChecked);
}


// Helpers
function toggleSignIn(button, input, isActive) {
  button.toggleAttribute("disabled", !isActive);
  input.toggleAttribute("disabled", !isActive);
}

function toggleTab(tab, isActive) {
  tab.classList.toggle("sub-center__tabs-tab--active", isActive);
  tab.setAttribute("aria-selected", String(isActive));
  tab.setAttribute("tabindex", isActive ? "0" : "-1");
}

function togglePanel(panel, isActive) {
  panel.classList.toggle("sub-center__tabs-panel--active", isActive);
  panel.toggleAttribute("hidden", !isActive);
  panel.setAttribute("tabindex", isActive ? "0" : "-1");
}

function getActiveTabIndex(tabs) {
  return [...tabs].findIndex(t => t.getAttribute("aria-selected") === "true");
}

